import dayjs from 'dayjs';
import { parseApiDate } from 'src/utils/taskDateUtils';

// Pause-aware due date for Task Board bucketing / overdue styling.
//
// The backend exposes `effective_due_at` (apps.tasks.due_windows): when every
// open checklist item is paused, it is the earliest pause end; null when the
// task is paused indefinitely. A task paused until next week therefore belongs
// in a later column, not Overdue, even though its original `due_at` has passed
// (Reading RCA 2026-10-19, review of BE #6942).
//
// Older backends don't send the field — fall back to `due_at` so nothing
// regresses during a staggered FE/BE deploy.

/** Raw value to bucket on: effective due when the BE sent it, else due_at. */
export function effectiveDueOf(task) {
    if (!task) return null;
    if (Object.prototype.hasOwnProperty.call(task, 'effective_due_at')) {
        return task.effective_due_at;
    }
    return task.due_at ?? null;
}

/** True when the task has a due date but is held back by an open-ended pause. */
export function isPausedIndefinitely(task) {
    return Boolean(
        task
        && task.due_at
        && Object.prototype.hasOwnProperty.call(task, 'effective_due_at')
        && task.effective_due_at == null
    );
}

// Legacy raw due_at window for each pause-aware bucket — the exact params the
// FE sent before due_bucket existed (Europe/London calendar days).
const LEGACY_WINDOWS = {
    overdue: (d) => ({ due_at__lt: d.startOf('day') }),
    today: (d) => ({ due_at__gte: d.startOf('day'), due_at__lte: d.endOf('day') }),
    tomorrow: (d) => ({ due_at__gte: d.add(1, 'day').startOf('day'), due_at__lt: d.add(2, 'day').startOf('day') }),
    later: (d) => ({ due_at__gte: d.add(2, 'day').startOf('day') }),
    upcoming: (d) => ({ due_at__gte: d.add(1, 'day').startOf('day') }),
    week: (d) => ({ due_at__gte: d.startOf('day'), due_at__lte: d.endOf('day').add(7, 'day') }),
};

/**
 * Task-list params for one board bucket: the pause-aware `due_bucket` PLUS the
 * legacy raw due_at window.
 *
 * Why both (review of FE #4674 / BE #6944): a backend that predates
 * due_bucket silently ignores it, and without the raw window every column
 * would return ALL open tasks (e.g. pre-production, which uses the production
 * backend, before BE #6944 is promoted). With both, an older backend applies
 * the raw window (the old behaviour) and a newer one lets due_bucket win and
 * drops the raw window. Deploy order then doesn't matter.
 *
 * @param {string} bucket  overdue | today | tomorrow | later | upcoming | week
 * @param {Function} dayjsLib  the dayjs function (injected for tests)
 */
export function dueBucketParams(bucket, dayjsLib) {
    const legacy = LEGACY_WINDOWS[bucket];
    if (!legacy) return {};
    const window = legacy(dayjsLib());
    const out = { due_bucket: bucket };
    Object.entries(window).forEach(([k, v]) => { out[k] = v.toISOString(); });
    return out;
}

/**
 * True when the task's effective due is in the past (drives the red overdue
 * colouring on board cards). Parses with parseApiDate: the API sends
 * "DD-MM-YYYY HH:MM:SS.ffffffZ", which plain dayjs() misreads
 * ("01-11-2026" → 11 January), colouring a future card as overdue.
 */
export function isEffectivelyOverdue(task, nowRef = null) {
    const raw = effectiveDueOf(task);
    if (!raw) return false;
    const due = parseApiDate(raw);
    if (!due || !due.isValid()) return false;
    return due.isBefore(nowRef ? dayjs(nowRef) : dayjs());
}
